// Falhas por provider (§4.1) — ícone do canal em tom destrutivo,
// motivo mais comum e o NÚMERO de erros em destaque (pill). Cada
// linha é um link → Consulta filtrada por status=FAILED + provider
// + channel.
import {
  AlertTriangleIcon,
  InfoIcon,
  MailIcon,
  MessageCircleIcon,
  SmartphoneIcon,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import type { NotificationOverviewProviderFailureType } from "@/types/notification-center"

import {
  buildNotificationSearchHref,
  formatDashboardInteger,
} from "../dashboard-utils"
import { DashboardChartCard } from "./dashboard-chart-card"

interface DashboardFailuresChartProps {
  failuresByProvider?: NotificationOverviewProviderFailureType[]
  isLoading: boolean
  hasError: boolean
  onRetry: () => void
  range: { startDate?: string; endDate?: string }
}

export const DashboardFailuresChart = ({
  failuresByProvider,
  isLoading,
  hasError,
  onRetry,
  range,
}: DashboardFailuresChartProps) => {
  const { t, i18n } = useTranslation()
  const chartPrefix =
    "pages.notificationCenter.dashboardPage.charts.failuresByProvider"

  const maxFailed = Math.max(
    ...(failuresByProvider ?? []).map(
      (item) => item.failed
    ),
    1
  )

  return (
    <DashboardChartCard
      title={t(`${chartPrefix}.title`)}
      testId="dashboard-failures"
      isLoading={isLoading}
      hasError={hasError}
      isEmpty={!failuresByProvider?.length}
      // Vazio aqui é notícia BOA — mensagem específica.
      emptyMessage={t(`${chartPrefix}.empty`)}
      onRetry={onRetry}
    >
      <ul className="space-y-1">
        {(failuresByProvider ?? []).map((item) => (
          <li key={`${item.provider}-${item.channel}`}>
            <Link
              to={buildNotificationSearchHref({
                status: "FAILED",
                provider: item.provider,
                channel: item.channel,
                ...range,
              })}
              aria-label={t(`${chartPrefix}.link`, {
                provider: item.provider,
                channel: item.channel,
              })}
              className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            >
              {/* Ícone do canal em círculo soft destrutivo. */}
              <span
                aria-hidden="true"
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600"
              >
                {item.channel === "EMAIL" ? (
                  <MailIcon className="size-4" />
                ) : item.channel === "WHATSAPP" ? (
                  <MessageCircleIcon className="size-4" />
                ) : (
                  <SmartphoneIcon className="size-4" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate font-medium group-hover:underline">
                    {item.provider}
                  </span>
                  <Badge
                    variant="outline"
                    className="shrink-0"
                  >
                    {item.channel}
                  </Badge>
                </span>
                <span className="mt-0.5 flex items-center gap-1 truncate text-xs text-muted-foreground">
                  <InfoIcon
                    aria-hidden="true"
                    className="size-3 shrink-0"
                  />
                  {t(`${chartPrefix}.reason`)}:{" "}
                  {item.reasonTop}
                </span>
                <span
                  aria-hidden="true"
                  className="mt-1 block h-1.5 rounded-full bg-red-500/80"
                  style={{
                    width: `${Math.max((item.failed / maxFailed) * 100, 2)}%`,
                  }}
                />
              </span>
              {/* Número de erros em destaque. */}
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-base font-bold tabular-nums text-red-700">
                <AlertTriangleIcon
                  aria-hidden="true"
                  className="size-3.5"
                />
                {formatDashboardInteger(
                  item.failed,
                  i18n.language
                )}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </DashboardChartCard>
  )
}
